import type { CSSProperties, MaybeRef, Ref } from 'vue';
import { computed, onUnmounted, ref, unref, watch } from 'vue';

interface AdjustPaddingCenteredOptions {
  minWidth?: number;
}

export const useAdjustPaddingCentered = (
  element: Ref<HTMLElement | null>,
  options: MaybeRef<AdjustPaddingCenteredOptions> = {},
) => {
  const contentRect = ref<DOMRect>();
  const wrapperRect = ref<DOMRect>();

  const updateRects = () => {
    if (!element.value) return;

    contentRect.value = element.value.getBoundingClientRect();
    wrapperRect.value = element.value.parentElement?.getBoundingClientRect();
  };

  const resizeObserver = new ResizeObserver(updateRects);

  const centeredPadding = computed<CSSProperties>(() => {
    const { minWidth = 0 } = unref(options);

    const wrapperCenter = (wrapperRect.value?.x || 0) + (wrapperRect.value?.width || 0) / 2;
    const contentCenter = (contentRect.value?.x || 0) + (contentRect.value?.width || 0) / 2;

    const offset = Math.min(
      Math.abs(wrapperCenter - contentCenter) * 2,
      Math.max((contentRect.value?.width || 0) - minWidth, 0),
    );
    const paddingLeft = contentCenter < wrapperCenter ? `${offset}px` : '0px';
    const paddingRight = contentCenter > wrapperCenter ? `${offset}px` : '0px';

    return { paddingLeft, paddingRight };
  });

  watch(
    element,
    (content, prevContent) => {
      if (prevContent) {
        resizeObserver.unobserve(prevContent);
      }
      if (content) {
        resizeObserver.observe(content);
        updateRects();
      }
    },
    { immediate: true },
  );

  onUnmounted(() => {
    resizeObserver.disconnect();
  });

  return { centeredPadding };
};
